"use client"

import { useState, useEffect, useRef, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { motion } from "motion/react"
import { TopsoilForm, type TopsoilInitialValues, type TopsoilOrder } from "@/components/tools/topsoil-form"
import {
  HeadlineHero,
  RelatedTools,
  ResultEmailCaptureCard,
  ToolPageLoadingFallback,
} from "@/components/tools/shared"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { type TopDressingMaterial } from "@/lib/top-dressing/calculator"
import { analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { buildToolUrl } from "@/lib/result-tool-urls"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"

const MATERIAL_PARAM: Record<string, TopDressingMaterial> = {
  topsoil: "topsoil",
  soil: "topsoil",
  sand: "sand",
  levelingsand: "sand",
  leveling: "sand",
  compost: "compost",
  soilmix: "soil_mix",
  soil_mix: "soil_mix",
  mix: "soil_mix",
  "7030": "soil_mix",
  peat: "peat",
  peatmoss: "peat",
}

function normalizeMaterial(raw: string): TopDressingMaterial | undefined {
  const key = raw.toLowerCase().replace(/[-_.\s]+/g, "")
  return MATERIAL_PARAM[key]
}

function TopsoilContent() {
  const searchParams = usePrerenderSafeSearchParams()
  const [initialValues, setInitialValues] = useState<TopsoilInitialValues | null>(null)
  const [liveOrder, setLiveOrder] = useState<TopsoilOrder | null>(null)
  const trackedRef = useRef(false)

  // URL prefill: ?zip & size & material
  useEffect(() => {
    const zipParam = searchParams.get("zip")
    const sizeParam = searchParams.get("size")
    const materialParam = searchParams.get("material")

    if (!zipParam && !sizeParam && !materialParam) return

    const size = sizeParam ? parseInt(sizeParam, 10) : undefined
    const next: TopsoilInitialValues = {
      zipCode: zipParam && /^\d{5}$/.test(zipParam) ? zipParam : undefined,
      lawnSize: size && !isNaN(size) ? size : undefined,
      material: materialParam ? normalizeMaterial(materialParam) : undefined,
    }
    setInitialValues(next)
  }, [searchParams])

  // Fire calculator_used once, when the first usable order appears.
  useEffect(() => {
    if (!liveOrder || trackedRef.current || liveOrder.areaSqFt <= 0) return
    trackedRef.current = true
    analytics.calculatorUsed("topsoil", {
      inputs: {
        material: liveOrder.material,
        depth_inches: liveOrder.depthInches,
        region: liveOrder.region ?? undefined,
      },
      result: {
        cubic_yards: liveOrder.volumeCubicYards,
        bags_40lb: liveOrder.bags40lb,
      },
      source: "live_update",
    })
  }, [liveOrder])

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Topsoil & Lawn Leveling Calculator" },
          ]}
        />

        <div className="max-w-7xl mx-auto">
          <div className="relative mb-8 md:mb-12">
            <HeadlineHero
              eyebrow="Topdressing & leveling"
              title="How many cubic yards of"
              accent="topsoil or sand?"
              subtitle="Pick your material and depth, enter your lawn size. You'll get cubic yards, weight, bag counts, and an honest bulk-vs-bagged cost comparison for your region."
            />
          </div>

          <div className="mb-8">
            <TopsoilForm initialValues={initialValues} onResultChange={setLiveOrder} />
          </div>

          {liveOrder && liveOrder.areaSqFt >= 100 && (
            <div className="mb-8">
              <ResultEmailCaptureCard
                source="tool_result_topsoil"
                toolDisplayName="topdressing plan"
                subcopy="Save your material list and get our lawn leveling guide, sent to your inbox."
                zipCode={initialValues?.zipCode}
                accountLead={{
                  redirectTo: buildToolUrl("topsoil-calculator", {
                    zip: initialValues?.zipCode,
                    lawnSize: initialValues?.lawnSize,
                  }),
                  headline: "Save my topdressing plan",
                  subcopy: "Keep this topdressing plan in a free account and track your lawn over time.",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_topsoil"),
                }}
                successSubcopy="Your lawn tips are on the way. Your topdressing plan stays right here to revisit anytime."
                onCaptured={() => {}}
              />
            </div>
          )}

          <RelatedTools
            currentToolId="topsoil-calculator"
            prefillValues={{
              zip: initialValues?.zipCode,
              lawnSize: initialValues?.lawnSize,
            }}
          />
        </div>
      </Reveal>
    </div>
  )
}

export function TopsoilCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="topsoil-calculator" />}>
      <SearchParamsHydrationGate>
        <TopsoilContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
